/**
 * PURPOSE:
 *   Centralizar el catálogo configurable de tabs públicos para deportes individuales.
 *
 * RESPONSIBILITIES:
 *   - Definir los códigos oficiales que se guardan en config_category.tabs_config_publico.
 *   - Normalizar/parsing/serialización del string de DB.
 *   - Mantener el default de individuales sin acoplarlo al card de configuración.
 *
 * ROUTE:
 *   src/utils/publicSportTabsConfig.js
 */

export const PUBLIC_SPORT_TAB_CODES = Object.freeze({
  EQUIPOS: "1",
  FIXTURE: "2",
  PROGRAMACION: "3",
  RESULTADOS: "4",
  ESTADISTICA: "5",
  INFORMACION: "6",
  PRUEBAS: "7",
  CATEGORIAS: "8",
  TABLA_FINAL: "9",
  MEDALLERO: "10",
});

export const PUBLIC_SPORT_TABS = Object.freeze([
  {
    code: PUBLIC_SPORT_TAB_CODES.EQUIPOS,
    label: "Equipos",
    description: "Instituciones o registrados del deporte.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.FIXTURE,
    label: "Fixture",
    description: "Llaves o fixture público. Normalmente solo colectivos.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.PROGRAMACION,
    label: "Programación",
    description: "Programación pública del deporte.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.RESULTADOS,
    label: "Resultados",
    description: "Resultados públicos / resultados documentarios.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.ESTADISTICA,
    label: "Estadística",
    description: "Estadísticas públicas. Normalmente solo colectivos.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.INFORMACION,
    label: "Información",
    description: "Información general del deporte.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.PRUEBAS,
    label: "Pruebas",
    description: "Pruebas del deporte individual.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.CATEGORIAS,
    label: "Categorías",
    description: "Categorías públicas del deporte.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.TABLA_FINAL,
    label: "Tabla Final",
    description: "Tabla final o ranking consolidado.",
  },
  {
    code: PUBLIC_SPORT_TAB_CODES.MEDALLERO,
    label: "Medallero",
    description: "Medallero público del deporte.",
  },
]);

export const DEFAULT_INDIVIDUAL_PUBLIC_TAB_CODES = Object.freeze([
  PUBLIC_SPORT_TAB_CODES.EQUIPOS,
  PUBLIC_SPORT_TAB_CODES.INFORMACION,
  PUBLIC_SPORT_TAB_CODES.PROGRAMACION,
  PUBLIC_SPORT_TAB_CODES.RESULTADOS,
]);

export const PUBLIC_SPORT_TAB_CODE_SET = new Set(
  PUBLIC_SPORT_TABS.map((tab) => tab.code)
);

export const PUBLIC_SPORT_TAB_BY_CODE = PUBLIC_SPORT_TABS.reduce(
  (acc, tab) => {
    acc[tab.code] = tab;
    return acc;
  },
  {}
);

export const getPublicSportTabLabel = (code) => {
  return PUBLIC_SPORT_TAB_BY_CODE[String(code)]?.label || `Tab ${code}`;
};

export const normalizePublicTabCodes = (codes = []) => {
  const normalizedCodes = [];
  const usedCodes = new Set();

  codes.forEach((code) => {
    const normalizedCode = String(code || "").trim();

    if (!PUBLIC_SPORT_TAB_CODE_SET.has(normalizedCode)) {
      return;
    }

    if (usedCodes.has(normalizedCode)) {
      return;
    }

    usedCodes.add(normalizedCode);
    normalizedCodes.push(normalizedCode);
  });

  return normalizedCodes;
};

export const parseTabsConfigPublico = (rawConfig) => {
  if (rawConfig === null || rawConfig === undefined) {
    return [];
  }

  return normalizePublicTabCodes(String(rawConfig).split(","));
};

export const serializeTabsConfigPublico = (codes = []) => {
  return normalizePublicTabCodes(codes).join(",");
};

export const resolveIndividualPublicTabCodesForForm = (rawConfig) => {
  const parsedCodes = parseTabsConfigPublico(rawConfig);

  return parsedCodes.length
    ? parsedCodes
    : [...DEFAULT_INDIVIDUAL_PUBLIC_TAB_CODES];
};

export const isUsingDefaultIndividualPublicTabs = (rawConfig) => {
  return parseTabsConfigPublico(rawConfig).length === 0;
};
